import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from "utils";

export const premiseList = createAsyncThunk('premise/list', async ({ pageable: data, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `premise`,
            params: {
                'pageSize': data?.pageSize,
                'pageNo': data?.currentPage,
                'search': data?.search,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const premiseDetails = createAsyncThunk('premise/details', async ({ id, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `premise/${id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const premiseDeepDive = createAsyncThunk('premise/deepDive', async ({ premise_id, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + 'deep_dive_premises',
            params: { premise_id },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addPremise = createAsyncThunk('premise/add', async (data) => {
    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'premise',
            data: {
                "name": data?.values?.name,
                "phone": data?.values?.phone,
                "city_id": data?.values?.city,
                "pincode": data?.values?.pincode,
                "address": data?.values?.address,
                "landmark": data?.values?.landmark,
                "no_of_zones": data?.values?.zones,
                "modules": data?.values?.modules,
                "account_allowed": data?.values?.account_control,
                "mfa_allowed": data?.values?.mfa_allowed,
                "defaulters_allowed": data?.values?.defaulters_allowed,
                "email_allowed": data?.values?.email_setup,
                "sms_allowed": data?.values?.sms_setup,
                "gateway_allowed": data?.values?.payment_gateway_setup,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const updatePremise = createAsyncThunk('premise/update', async (data) => {
    try {
        const res = await axios({
            method: 'PUT',
            url: getConfig.API_URL + 'premise',
            data: {
                "premise_id": data?.values?.id,
                "name": data?.values?.name,
                "phone": data?.values?.phone,
                "city_id": data?.values?.city,
                "pincode": data?.values?.pincode,
                "address": data?.values?.address,
                "landmark": data?.values?.landmark,
                "no_of_zones": data?.values?.zones,
                "modules": data?.values?.modules,
                "account_allowed": data?.values?.account_control,
                "mfa_allowed": data?.values?.mfa_allowed,
                "defaulters_allowed": data?.values?.defaulters_allowed,
                "email_allowed": data?.values?.email_setup,
                "sms_allowed": data?.values?.sms_setup,
                "gateway_allowed": data?.values?.payment_gateway_setup,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const delPremise = createAsyncThunk('premise/delete', async (data) => {
    try {
        const res = await axios({
            method: 'DELETE',
            url: getConfig.API_URL + `premise/${data?.id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const premiseByUser = createAsyncThunk('premise/byUser', async ({ navigate } = {}) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `premise_by_user`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const switchPremiseByUser = createAsyncThunk('premise/switchByUser', async ({ premise_id, unit_id, navigate }) => {
    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + `premise_by_user`,
            data: {
                premise_id,
                unit_id,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

const premise = createSlice({
    name: 'premise',
    initialState: {
        list: [],
        byUserList: [],
        details: null,
        loading: false,
        byUserLoading: false,
    },
    extraReducers: (builder) => {
        builder.addCase(premiseList.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(premiseList.fulfilled, (state, action) => {
            state.loading = false;
            if (action?.payload?.status === 200) {
                state.list = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.list = []
            }
        });
        builder.addCase(premiseList.rejected, (state, action) => {
            state.loading = false;
        })

        builder.addCase(premiseDetails.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.details = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.details = null
            }
        });

        builder.addCase(premiseByUser.pending, (state) => {
            state.byUserLoading = true;
        });
        builder.addCase(premiseByUser.fulfilled, (state, action) => {
            state.byUserLoading = false;
            if (action?.payload?.status === 200) {
                state.byUserList = action.payload.data.data || [];
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg?.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg?.navigate)
            } else {
                state.byUserList = []
            }
        });
        builder.addCase(premiseByUser.rejected, (state) => {
            state.byUserLoading = false;
            state.byUserList = []
        });
    }
})

export default premise.reducer;